import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cheapestTariff } from 'reactApp/appHelpers/featuresHelpers/features/cheapestTariff';
import { favorableTariffs } from 'reactApp/appHelpers/featuresHelpers/features/favorableTariffs';
import { overquotaCleanerBanner } from 'reactApp/appHelpers/featuresHelpers/features/overquotaCleanerBanner';
import { getPaySource } from 'reactApp/modules/payment/payment.analytics';
import { openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { QuotaLandingSelectors } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { QUOTA_URL } from 'reactApp/sections/QuotaLanding/QuotaLanding.data';
import { getQuotaBillingSource, sendQuotaGa } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import { revealPlatformPopup } from 'reactApp/sections/QuotaLanding/TariffsPlans/TariffPlansCard/TariffPlansCard.helpers';
import { getTariffsList } from 'reactApp/sections/QuotaLanding/TariffsPlans/TariffsPlans.selectors';
import type { RootState } from 'reactApp/store';
import type { ITariffsByTab } from 'reactApp/ui/TariffSectionNew/TariffsSection.types';

/**
 * Хук покупки рекомендованного тарифа "Единой квоты"
 * Используется в чистилке как тариф по умолчанию
 */
export const useBuyCleanerTariff = (
    isMobilePayAvailable = false,
    isIOSPromo = false,
    isAndroidPromo = false
): {
    tariffId?: string;
    tariffPrice?: number;
    tariffValue?: string;
    onBuy: () => void;
    isUserOverquota?: boolean;
} => {
    const dispatch = useDispatch();
    const tariffList = useSelector((state: RootState) => getTariffsList(state, false));
    const isMobile = useSelector(QuotaLandingSelectors.isMobile);
    const isUserOverquota = useSelector(UserQuotaSelectors.isOverquotaBySpaceAndApi);

    const recommendedTariff: Partial<ITariffsByTab> = useMemo(() => {
        // tempexp_16598-start
        if (cheapestTariff) {
            const recommended = favorableTariffs.length ? 'W1T' : 'W256G';
            return (
                (tariffList as ITariffsByTab[])?.find(
                    (tariff) => tariff?.products?.monthProduct && tariff.products.monthProduct?.id.includes(recommended)
                ) || {}
            );
        }
        // tempexp_16598-end
        return (tariffList as ITariffsByTab[])?.find((tariff) => tariff.isRecommended && tariff.products.monthProduct) || {};
    }, [tariffList]);

    const { id, price, discountPrice, space } = recommendedTariff?.products?.monthProduct || {};

    const onSuccess = useCallback(() => {
        sendQuotaGa({ action: 'tariff', label: 'buy', tariff: id, amount: discountPrice || price });
    }, [id, price, discountPrice]);

    const onBuy = useCallback(() => {
        if ((isAndroidPromo || isIOSPromo) && !isMobilePayAvailable && id && isMobile) {
            revealPlatformPopup(id, isIOSPromo, isAndroidPromo);
            return;
        }
        dispatch(
            openTariffBuy({
                productId: isUserOverquota && overquotaCleanerBanner?.tariff ? overquotaCleanerBanner?.tariff : id,
                source: getQuotaBillingSource(),
                onSuccess,
                isMobile,
                isQuotaCleaner: true,
                paySource: getPaySource(isUserOverquota && overquotaCleanerBanner?.variant ? 'banner_overquota_cleaner' : 'cleaner_promo'),
                onError: () => open(QUOTA_URL),
            })
        );
    }, [dispatch, id, onSuccess, isUserOverquota]);

    return {
        tariffId: id,
        tariffPrice: discountPrice || price,
        tariffValue: space?.value,
        isUserOverquota,
        onBuy,
    };
};
